import React, { useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Feed } from "@mui/icons-material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { dosyaAdi } from "../../utils/exportUtils";
import api from "../../api";

import { FIZYOGRAFYA_OPTIONS } from "../../constants/fieldConstants";

// --- GÖRSEL YARDIMCI: KESİK ÇİZGİLİ ÇERÇEVE ---
const applyOuterFrame = (sheet, startRow, endRow, startCol, endCol) => {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cell = sheet.getCell(r, c);
      const border = { ...(cell.border || {}) };
      if (r === startRow)
        border.top = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (r === endRow)
        border.bottom = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (c === startCol)
        border.left = { style: "mediumDashed", color: { argb: "FF000000" } };
      if (c === endCol)
        border.right = { style: "mediumDashed", color: { argb: "FF000000" } };
      cell.border = border;
    }
  }
};

// =====================================================================
// YARDIMCI COMPONENT 1: Detaylı Özellikler Tablosu (Ön Yüz)
// =====================================================================
const appendDetayliOzelliklerTablosu = (ws, startRow, d) => {
  const t = (deger, hedef, haric) => {
    if (!deger) return "□";
    const check = (v) => {
      const str = String(v).toLowerCase();
      const hasTarget = str.includes(hedef.toLowerCase());
      if (haric) return hasTarget && !str.includes(haric.toLowerCase());
      return hasTarget;
    };
    if (Array.isArray(deger)) return deger.some(check) ? "■" : "□";
    return check(deger) ? "■" : "□";
  };

  const bakiVal = String(d.egim_yonu || "")
    .toUpperCase()
    .trim();

  // 2. Hangi yönlerin aktif olduğunu bulalım (İçinde geçiyorsa true döner)
  // Bu sayede "Kuzeydoğu" gelirse hem K hem D aktif olur.
  const isK =
    bakiVal.includes("KUZEY") ||
    bakiVal === "K" ||
    bakiVal.includes("KD") ||
    bakiVal.includes("KB");
  const isG =
    bakiVal.includes("GÜNEY") ||
    bakiVal.includes("GUNEY") ||
    bakiVal === "G" ||
    bakiVal.includes("GD") ||
    bakiVal.includes("GB");
  const isD =
    bakiVal.includes("DOĞU") ||
    bakiVal.includes("DOGU") ||
    bakiVal === "D" ||
    bakiVal.includes("KD") ||
    bakiVal.includes("GD");
  const isB =
    bakiVal.includes("BATI") ||
    bakiVal === "B" ||
    bakiVal.includes("KB") ||
    bakiVal.includes("GB");

  // 3. Renkleri belirleyelim: Seçiliyse Belirgin Kırmızı, değilse Soluk Gri
  const renkK = isK ? "FFD32F2F" : "FFD9D9D9";
  const renkG = isG ? "FFD32F2F" : "FFD9D9D9";
  const renkD = isD ? "FFD32F2F" : "FFD9D9D9";
  const renkB = isB ? "FFD32F2F" : "FFD9D9D9";
  const renkMerkez = "FF000000"; // Ortadaki artı (+) işareti siyah kalsın

  const checkDerinlik = (min, max) => {
    const val = d.tesirli_derinlik;
    if (val === null || val === undefined || val === "") return "□";
    const num = parseFloat(val);
    if (isNaN(num)) return "□";
    if (max === null) return num > min ? "■" : "□";
    return num >= min && num <= max ? "■" : "□";
  };

  const getMappedSlope = (rawSlope) => {
    if (!rawSlope) return "";
    const s = String(rawSlope).trim();
    if (s === "0-2") return "0-2";
    if (["2-4", "4-6", "2-6"].includes(s)) return "2-6";
    if (["6-8", "8-10", "10-12", "6-12"].includes(s)) return "6-12";
    if (["12-14", "14-16", "16-18", "18-20", "12-20"].includes(s))
      return "12-20";
    if (s === "20-30") return "20-30";
    if (s === "30-45") return "30-45";
    if (s === "45+" || s === ">45") return ">45";
    return s;
  };
  const mappedSlope = getMappedSlope(d.egim_yuzde);

  const headerFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { arg: "FFEAEAEA" },
  };
  let r = startRow;

  // --- Satır 0 ---
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value = "Arazi Kullanımı";
  ws.mergeCells(r, 16, r, 24);
  ws.getCell(r, 16).value = "Ana Materyal Niteliği";
  ws.mergeCells(r, 25, r, 47);
  ws.getCell(r, 25).value = "Tesirli Toprak Derinliği (cm)";
  ws.mergeCells(r, 48, r, 56);
  ws.getCell(r, 48).value = "Eğim (%)";
  [1, 16, 25, 48].forEach((c) => {
    ws.getCell(r, c).font = { bold: true, size: 8 };
    ws.getCell(r, c).alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell(r, c).fill = headerFill;
  });

  // --- Satır 1 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = "";
  ws.mergeCells(r, 4, r, 10);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Diğer")} Diğer Araziler ➔`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value =
    `${t(d.arazi_kullanim_detay, "Çıplak Kaya")} Çıplak Kaya`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Aluviyal")} Aluviyal`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Volkan Külü")} Volkan Külü`;

  ws.mergeCells(r, 25, r, 31);
  ws.getCell(r, 25).value = `${checkDerinlik(150, null)} Çok Derin (150+)`;
  ws.mergeCells(r, 32, r, 38);
  ws.getCell(r, 32).value = `${checkDerinlik(91, 150)} Derin (91-150)`;
  ws.mergeCells(r, 39, r, 47);
  ws.getCell(r, 39).value = `${checkDerinlik(51, 90)} Orta Derin (51-90)`;

  ws.mergeCells(r, 48, r, 51);
  ws.getCell(r, 48).value = `${t(mappedSlope, "0-2")} 0-2`;
  ws.mergeCells(r, 52, r, 56);
  ws.getCell(r, 52).value = `${t(mappedSlope, "20-30")} 20-30`;

  // --- Satır 2 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = `${t(d.arazi_kullanim_sinif, "sulu")} sulu`;
  ws.mergeCells(r, 4, r, 10);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Özel Ürün")} Özel Ürün`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.arazi_kullanim_detay, "Yerleşim")} Yerleşim`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Koluviyal")} Koluviyal`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Lös")} Lös`;

  ws.mergeCells(r, 25, r, 35);
  ws.getCell(r, 25).value = `        ${checkDerinlik(21, 50)} Sığ (21-50)`;
  ws.mergeCells(r, 36, r, 47);
  ws.getCell(r, 36).value = `      ${checkDerinlik(0, 20)} Çok Sığ (0-20)`;

  ws.mergeCells(r, 48, r, 51);
  ws.getCell(r, 48).value = `${t(mappedSlope, "2-6")} 2-6`;
  ws.mergeCells(r, 52, r, 56);
  ws.getCell(r, 52).value = `${t(mappedSlope, "30-45")} 30-45`;

  // --- Satır 3 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = "";
  ws.mergeCells(r, 4, r, 10);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Dikili Tarım")} Dikili Tarım`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.arazi_kullanim_detay, "Orman")} Orman`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Bazalt")} Bazalt`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Proklastik")} Proklastik`;

  ws.mergeCells(r, 25, r, 47);
  ws.getCell(r, 25).value =
    `Profilde Nem Durumu ve Belirtile : ${d.profil_nem || "..................."}`;
  ws.getCell(r, 25).font = { bold: true, size: 8 };

  ws.mergeCells(r, 48, r, 51);
  ws.getCell(r, 48).value = `${t(mappedSlope, "6-12")} 6-12`;
  ws.mergeCells(r, 52, r, 56);
  ws.getCell(r, 52).value = `${t(mappedSlope, ">45")} >45`;

  // --- Satır 4 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = `${t(d.arazi_kullanim_sinif, "kuru")} kuru`;
  ws.mergeCells(r, 4, r, 10);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Marjinal Tarım")} Marjinal Tarım`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.arazi_kullanim_detay, "Çayır")} Çayır`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Organik")} Organik`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Marn")} Marn`;
  ws.mergeCells(r, 25, r, 47);
  ws.getCell(r, 25).value =
    "....................................................................................";

  ws.mergeCells(r, 48, r, 51);
  ws.getCell(r, 48).value = `${t(mappedSlope, "12-20")} 12-20`;
  ws.mergeCells(r, 52, r, 56);
  ws.getCell(r, 52).value = "";

  // --- Satır 5 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = "";
  ws.mergeCells(r, 4, r, 10);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Örtü Altı")} Örtü Altı Tarımı`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.arazi_kullanim_detay, "Mera")} Mera`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Aeolian")} Aeolian`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Sabrolit")} Sabrolit`;

  ws.mergeCells(r, 25, r + 1, 28);
  ws.getCell(r, 25).value = "Taban Suyu";
  ws.getCell(r, 25).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  ws.mergeCells(r, 29, r, 40);
  ws.getCell(r, 29).value = `En Düşük: ${d.taban_suyu_min || "......."} cm`;

  ws.mergeCells(r, 41, r, 47);
  ws.getCell(r, 41).value = "Tuzluluk ve Alkalilik";
  ws.getCell(r, 41).font = { bold: true, size: 8 };
  ws.getCell(r, 41).alignment = { horizontal: "center" };
  ws.getCell(r, 41).fill = headerFill;

  ws.mergeCells(r, 48, r, 56);
  ws.getCell(r, 48).value = ".........................................";

  // --- Satır 6 ---
  r++;
  ws.mergeCells(r, 1, r, 3);
  ws.getCell(r, 1).value = "";
  ws.mergeCells(r, 4, r, 9);
  ws.getCell(r, 4).value =
    `${t(d.arazi_kullanim_detay, "Mutlak Tarım")} Mutlak Tarım`;
  ws.mergeCells(r, 10, r, 15);
  ws.getCell(r, 10).value =
    `${t(d.arazi_kullanim_detay, "Irmak")} Irmak Taş. Yatk.`;

  ws.mergeCells(r, 16, r, 19);
  ws.getCell(r, 16).value = `${t(d.ana_materyal, "Lakustrin")} Lakustrin`;
  ws.mergeCells(r, 20, r, 24);
  ws.getCell(r, 20).value = `${t(d.ana_materyal, "Karışık")} Karışık`;

  ws.mergeCells(r, 29, r, 40);
  ws.getCell(r, 29).value = `En Yüksek: ${d.taban_suyu_max || "......."} cm`;
  ws.mergeCells(r, 41, r, 47);
  ws.getCell(r, 41).value = `${t(d.tuzluluk, "Yok")} Yok`;

  ws.mergeCells(r, 48, r, 56);
  ws.getCell(r, 48).value = "Eğim Yönü (Bakı)";
  ws.getCell(r, 48).font = { bold: true, size: 10 };
  ws.getCell(r, 48).alignment = { horizontal: "center" };
  ws.getCell(r, 48).fill = headerFill;

  // --- Satır 7 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value =
    `Açıklama: ${d.arazi_aciklama || "................"}`;
  ws.mergeCells(r, 10, r, 15);
  ws.getCell(r, 10).value =
    `${t(d.arazi_kullanim_detay, "Kıyı Kumulları")} Kıyı Kumulları`;

  ws.mergeCells(r, 16, r, 26);
  ws.getCell(r, 16).value =
    `Kaya Tipi : ${d.kaya_tipi || "................................"}`;
  ws.mergeCells(r, 27, r, 40);
  ws.getCell(r, 27).value =
    `Taban Suyu : ${d.taban_suyu || "................................"}`;
  ws.mergeCells(r, 41, r, 47);
  ws.getCell(r, 41).value = `${t(d.tuzluluk, "Az")} Az`;

  ws.mergeCells(r, 48, r + 6, 56);
  ws.getCell(r, 48).value = {
    richText: [
      { font: { bold: true, name: "Arial", size: 9 }, text: "K\n" },
      { font: { color: { argb: renkK }, size: 18 }, text: "▲\n" },
      { font: { bold: true, name: "Arial", size: 9 }, text: "B  " },
      { font: { color: { argb: renkB }, size: 20 }, text: "◄ " },
      { font: { color: { argb: renkMerkez }, size: 16 }, text: "╬" },
      { font: { color: { argb: renkD }, size: 20 }, text: " ►" },
      { font: { bold: true, name: "Arial", size: 9 }, text: "  D\n" },
      { font: { color: { argb: renkG }, size: 18 }, text: "▼\n" },
      { font: { bold: true, name: "Arial", size: 9 }, text: "G" },
    ],
  };
  ws.getCell(r, 48).alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };

  // --- Satır 8 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = `Ürün: ${d.urun || "........................"}`;
  ws.mergeCells(r, 10, r, 15);
  ws.getCell(r, 10).value =
    `${t(d.arazi_kullanim_detay, "Sazlık Bataklıklar", "Sazlık")} Sazlık Bataklıkla`;

  ws.mergeCells(r, 16, r, 26);
  ws.getCell(r, 16).value =
    `Mikrorölyef: ${d.mikrorolyef || "................................"}`;
  ws.mergeCells(r, 27, r, 40);
  ws.getCell(r, 27).value =
    `Belirtileri : ${d.taban_suyu_belirti || "................................"}`;
  ws.mergeCells(r, 41, r, 47);
  ws.getCell(r, 41).value = `${t(d.tuzluluk, "Orta")} Orta`;

  // --- Satır 9 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value =
    `Kök Gel. Elverişli Top. Der.: ${d.kok_gel_derinlik || "...................."} cm.`;
  ws.mergeCells(r, 16, r, 40);
  ws.getCell(r, 16).value =
    `Kök Gel. Eng.Fak. Türü ve Der.: ${d.kok_gel_eng_faktor || "...................."}`;
  ws.mergeCells(r, 41, r, 47);
  ws.getCell(r, 41).value = `${t(d.tuzluluk, "Çok")} Çok`;

  // --- Satır 10 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value = "Tabii Toprak Drenajı";
  ws.mergeCells(r, 16, r, 30);
  ws.getCell(r, 16).value = "Sel Basması";
  ws.mergeCells(r, 31, r, 47);
  ws.getCell(r, 31).value = "İnsan Etkisi";
  [1, 16, 31].forEach((c) => {
    ws.getCell(r, c).font = { bold: true, size: 8 };
    ws.getCell(r, c).alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell(r, c).fill = headerFill;
  });

  // --- Satır 11 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value =
    `${t(d.tabii_drenaj, "yok")} Aşırı  ${t(d.tabii_drenaj, "yararlı")} İyi  ${t(d.tabii_drenaj, "f1")} Orta  ${t(d.tabii_drenaj, "f2")} Yetersiz  ${t(d.tabii_drenaj, "f3")} Fena`;
  ws.getCell(r, 1).alignment = { vertical: "middle", horizontal: "center" };

  ws.mergeCells(r, 16, r, 30);
  ws.getCell(r, 16).value =
    `${t(d.sel_basmasi, "F1")} F1 ( arasıra sel alır. ekim zamanı gecikebilir)`;
  ws.mergeCells(r, 31, r, 36);
  ws.getCell(r, 31).value = `${t(d.insan_etkisi, "Sürüm")} Sürüm`;
  ws.mergeCells(r, 37, r, 41);
  ws.getCell(r, 37).value = `${t(d.insan_etkisi, "Sulama")} Sulama`;
  ws.mergeCells(r, 42, r, 47);
  ws.getCell(r, 42).value = `${t(d.insan_etkisi, "Drenaj")} Drenaj`;

  // --- Satır 12 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value =
    `Yüzey Drenajı: ${d.yuzey_drenaji || "........................................"}`;
  ws.mergeCells(r, 16, r, 30);
  ws.getCell(r, 16).value =
    `${t(d.sel_basmasi, "F2")} F2 (sıksık sel alır. mahsul sıksık zarar görür)`;
  ws.mergeCells(r, 31, r, 36);
  ws.getCell(r, 31).value = `${t(d.insan_etkisi, "Gübreleme")} Gübreleme`;
  ws.mergeCells(r, 37, r, 41);
  ws.getCell(r, 37).value = `${t(d.insan_etkisi, "Sedde")} Sedde`;
  ws.mergeCells(r, 42, r, 47);
  ws.getCell(r, 42).value = `${t(d.insan_etkisi, "Teraslama")} Teraslama`;

  // --- Satır 13 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value = "Taşkın Olma Olasılığı";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.getCell(r, 1).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 1).fill = headerFill;

  ws.mergeCells(r, 16, r, 30);
  ws.getCell(r, 16).value =
    `${t(d.sel_basmasi, "F3")} F3 (çok sık sel alır. Çok zarar. Ekonomik olmaz)`;
  ws.mergeCells(r, 31, r, 39);
  ws.getCell(r, 31).value = `${t(d.insan_etkisi, "Tesviye")} Tesviye`;
  ws.mergeCells(r, 40, r, 47);
  ws.getCell(r, 40).value =
    `[ ] Diğer: ${!["Sürüm", "Sulama", "Drenaj", "Gübreleme", "Sedde", "Teraslama", "Tesviye"].includes(d.insan_etkisi) ? d.insan_etkisi || "" : "........"}`;

  // --- Satır 14 ---
  r++;
  ws.mergeCells(r, 1, r, 4);
  ws.getCell(r, 1).value = `${t(d.taskin_olasiligi, "Var")} Var`;
  ws.mergeCells(r, 5, r, 15);
  ws.getCell(r, 5).value = `[ ] Diğer: ...........................`;

  ws.mergeCells(r, 16, r, 40);
  ws.getCell(r, 16).value = "Taşlılık - Çakıllılık - Kayalılık";
  ws.getCell(r, 16).font = { bold: true, size: 8 };
  ws.getCell(r, 16).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 16).fill = headerFill;

  ws.mergeCells(r, 41, r, 56);
  ws.getCell(r, 41).value = "Yüzeydeki Çatlaklar (cm)";
  ws.getCell(r, 41).font = { bold: true, size: 8 };
  ws.getCell(r, 41).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 41).fill = headerFill;

  // --- Satır 15 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value = `${t(d.taskin_olasiligi, "Yok")} Yok`;
  ws.mergeCells(r, 16, r, 40);
  ws.getCell(r, 16).value = "Taşlılık (Çapı 7,5-60cm)";
  ws.getCell(r, 16).font = { bold: true, size: 8 };
  ws.mergeCells(r, 41, r, 56);
  ws.getCell(r, 41).value =
    `${t(d.yuzey_catlak, "İnce")} İnce (<1)   ${t(d.yuzey_catlak, "Orta")} Orta (1-2)   ${t(d.yuzey_catlak, "Geniş")} Geniş (2-5)`;

  // --- Satır 16 ---
  r++;
  ws.mergeCells(r, 1, r, 15);
  ws.getCell(r, 1).value = "Erozyon Türü";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.getCell(r, 1).alignment = { horizontal: "center", vertical: "middle" };
  ws.getCell(r, 1).fill = headerFill;

  ws.mergeCells(r, 16, r, 40);
  ws.getCell(r, 16).value =
    `${t(d.taslilik, "T1")} T1 Hafif Taşlı (arazi yüzeyinin %2-10 kaplamış)`;
  ws.mergeCells(r, 41, r, 56);
  ws.getCell(r, 41).value =
    `${t(d.yuzey_catlak, "Çok Geniş")} Çok Geniş (5-10)   ${t(d.yuzey_catlak, "Aşırı")} Aşırı geniş (>10)`;

  // --- Satır 17 ---
  r++;
  ws.mergeCells(r, 1, r, 9);
  ws.getCell(r, 1).value = "Şekil:";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.mergeCells(r, 10, r, 15);
  ws.getCell(r, 10).value = "Derecesi:";
  ws.getCell(r, 10).font = { bold: true, size: 8 };

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value =
    `${t(d.taslilik, "T2")} T2 Orta Taşlı (arazi yüzeyinin %11-50 kaplamış)`;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = "Kayalılık (Çapı>60cm)";
  ws.getCell(r, 38).font = { bold: true, size: 8 };

  // --- Satır 18 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value = `${t(d.erozyon_sekli, "Rüzgar")} Rüzgar Erozyonu`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.su_erozyon_derecesi, "Hafif")} Hafif`;

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value =
    `${t(d.taslilik, "T3")} T3 Çok Taşlı (arazi yüzeyinin %51-90 kaplamış)`;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `${t(d.kayalilik, "R0")} R0 Az Kayalı (%0-5)`;

  // --- Satır 19 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value = `${t(d.erozyon_sekli, "Su")} Su Erozyonu`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `${t(d.su_erozyon_derecesi, "Orta")} Orta`;

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value = "Çakıllılık (Çapı 0,2-7,5cm)";
  ws.getCell(r, 16).font = { bold: true, size: 8 };
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `${t(d.kayalilik, "R1")} R1 Hafif Kayalı (%6-10)`;

  // --- Satır 20 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Yüzey")} Yüzey`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value =
    `${t(d.su_erozyon_derecesi, "şiddetli", "çok")} Şiddetli`;

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value = `${t(d.cakillilik, "Ç1")} Ç1 Hafif Taşlı (%2-10)`;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `${t(d.kayalilik, "R2")} R2 Orta Kayalı (%11-30)`;

  // --- Satır 21 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Oluk")} Oluk`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value =
    `${t(d.su_erozyon_derecesi, "çok şiddetli")} Çok Şiddetli`;

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value = `${t(d.cakillilik, "Ç2")} Ç2 Orta Taşlı (%11-50)`;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `${t(d.kayalilik, "R3")} R3 Çok Kayalı (%31-50)`;

  // --- Satır 22 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value = `    ${t(d.erozyon_sekli, "Tünel")} Tünel`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = `      (yarıntılı)`;

  ws.mergeCells(r, 16, r, 37);
  ws.getCell(r, 16).value = `${t(d.cakillilik, "Ç3")} Ç3 Çok Taşlı (%51-90)`;
  ws.mergeCells(r, 38, r, 56);
  ws.getCell(r, 38).value = `${t(d.kayalilik, "R4")} R4 Pek Çok Kayalı`;

  // --- Satır 23 ---
  r++;
  ws.mergeCells(r, 1, r, 10);
  ws.getCell(r, 1).value =
    `    ${t(d.erozyon_sekli, "Sel Yarıntısı")} Sel Yarıntısı`;
  ws.mergeCells(r, 11, r, 15);
  ws.getCell(r, 11).value = "";
  ws.mergeCells(r, 16, r, 56);
  ws.getCell(r, 16).value = "";

  // --- Satır 24 (Genel Düşünceler Başlık) ---
  r++;
  ws.mergeCells(r, 1, r, 56);
  ws.getCell(r, 1).value = "Genel Düşünceleriniz";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  ws.getCell(r, 1).alignment = { horizontal: "left", vertical: "middle" };
  ws.getCell(r, 1).fill = headerFill;

  // --- Satır 25-26 (Genel Düşünceler İçerik) ---
  const dusunceStartRow = r + 1;
  r += 2;
  ws.mergeCells(dusunceStartRow, 1, r, 56);
  ws.getCell(dusunceStartRow, 1).value = d.dusunceler || "";
  ws.getCell(dusunceStartRow, 1).alignment = {
    vertical: "top",
    horizontal: "left",
    wrapText: true,
  };

  // =========================================================
  // MATEMATİKSEL KUSURSUZ UYUM:
  // A4 Kâğıdı Net Yükseklik = ~566 Piksel
  // Tablo Toplam Satır = 36
  // 566 / 36 = 15.7
  // Tüm satırların yüksekliği TAM OLARAK 15.5 yapıldı. ASLA TAŞMAZ!
  // =========================================================
  for (let rowIdx = startRow; rowIdx <= r; rowIdx++) {
    ws.getRow(rowIdx).height = 15.5;
    for (let colIdx = 1; colIdx <= 56; colIdx++) {
      const cell = ws.getCell(rowIdx, colIdx);
      if (!cell.font) cell.font = { name: "Calibri", size: 9 };
      if (!cell.alignment)
        cell.alignment = { vertical: "middle", horizontal: "left" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    }
  }

  const drawBox = (r1, c1, r2, c2) => {
    for (let row = r1; row <= r2; row++) {
      for (let col = c1; col <= c2; col++) {
        const cell = ws.getCell(row, col);
        const border = { ...cell.border };
        if (row === r1) border.top = { style: "medium" };
        if (row === r2) border.bottom = { style: "medium" };
        if (col === c1) border.left = { style: "medium" };
        if (col === c2) border.right = { style: "medium" };
        cell.border = border;
      }
    }
  };

  const sr = startRow;
  drawBox(sr, 1, sr + 9, 15); // Arazi
  drawBox(sr, 16, sr + 9, 28); // Ana Materyal
  drawBox(sr, 29, sr + 4, 47); // Tesirli
  drawBox(sr + 5, 29, sr + 9, 40); // Taban Suyu
  drawBox(sr + 5, 41, sr + 9, 47); // Tuzluluk
  drawBox(sr, 48, sr + 5, 56); // Eğim
  drawBox(sr + 6, 48, sr + 12, 56); // Bakı
  drawBox(sr + 10, 1, sr + 12, 15); // Drenaj
  drawBox(sr + 10, 16, sr + 13, 35); // Sel
  drawBox(sr + 10, 36, sr + 12, 47); // İnsan Üst
  drawBox(sr + 13, 36, sr + 13, 56); // İnsan Alt
  drawBox(sr + 13, 1, sr + 14, 15); // Taşkın
  drawBox(sr + 15, 1, sr + 22, 15); // Erozyon
  drawBox(sr + 14, 16, sr + 22, 43); // Taşlılık
  drawBox(sr + 14, 44, sr + 16, 56); // Çatlaklar
  drawBox(sr + 17, 44, sr + 22, 56); // Kayalılık
  drawBox(sr + 23, 1, sr + 25, 56); // Düşünceler

  return r;
};

// =====================================================================
// YARDIMCI COMPONENT 2: Akıllı Horizon Seçici
// =====================================================================
const hlSmart = (visualLabels, dbValue, constantValues, sep) => {
  if (!dbValue || dbValue.length === 0) return visualLabels.join(sep);
  const dbValArr = Array.isArray(dbValue)
    ? dbValue.map((v) => String(v).trim())
    : [String(dbValue).trim()];
  return visualLabels
    .map((vLabel, i) => {
      const matchVal = constantValues[i]
        ? String(constantValues[i]).trim()
        : "";
      return dbValArr.some((v) => v === matchVal) ? `[ ${vLabel} ]` : vLabel;
    })
    .join(sep);
};

// =====================================================================
// ANA COMPONENT
// =====================================================================
export default function JobTemplateExportButton({ jobId, jobName }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/all-data");
      if (!res.data.success) throw new Error("Veriler alınamadı");

      const jobProfiles = res.data.data.filter((p) => p.job_id === jobId);

      if (jobProfiles.length === 0) {
        alert("Bu çalışmaya ait girilmiş profil bulunamadı.");
        setLoading(false);
        return;
      }

      jobProfiles.sort((a, b) => {
        const getNum = (str) => {
          const match = String(str || "").match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };
        return getNum(a.profil_no) - getNum(b.profil_no);
      });

      const workbook = new ExcelJS.Workbook();

      const ws = workbook.addWorksheet("Profiller ve Horizonlar", {
        views: [{ showGridLines: false, pageBreakPreview: true }],
        pageSetup: {
          paperSize: 9,
          orientation: "landscape",
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          margins: {
            left: 0.15,
            right: 0.15,
            top: 0.2,
            bottom: 0.2,
            header: 0,
            footer: 0,
          },
        },
      });

      ws.columns = Array.from({ length: 56 }, (_, i) => ({
        header: "",
        key: `col${i + 1}`,
        width: 2.8,
      }));

      let currentStartRow = 2;

      jobProfiles.forEach((profile) => {
        let d = {};
        try {
          d =
            typeof profile.full_data === "string"
              ? JSON.parse(profile.full_data)
              : profile.full_data;
        } catch (e) {
          d = {};
        }

        // ==============================================================
        // BÖLÜM 1: PROFİL TABLOSU (ÖN YÜZ)
        // ==============================================================
        let r = currentStartRow;
        const blockStartRow = r;

        ws.mergeCells(r, 1, r, 17);
        ws.getCell(r, 1).value =
          `Çalışmanın Adı : ${d.calismanin_adi || jobName || "..............................................."}`;

        ws.mergeCells(r, 18, r, 25);
        ws.getCell(r, 25).value =
          `Profil No : ${profile.profil_no || "..........."}`;

        ws.mergeCells(r, 26, r, 45);
        ws.getCell(r, 35).value =
          `Tanımlayan : ${d.tanimlayan || "........................................................"}`;

        ws.mergeCells(r, 46, r, 56);
        ws.getCell(r, 50).value =
          `Tarih : ${d.tarih ? d.tarih.split("-").reverse().join(".") : "....../....../.........."}`;

        ws.mergeCells(r + 1, 1, r + 1, 17);
        ws.getCell(r + 1, 1).value =
          `..............................................................`;

        ws.mergeCells(r + 1, 18, r + 1, 31);
        ws.getCell(r + 1, 18).value =
          `İklim : ${d.iklim || "...................................."}`;

        ws.mergeCells(r + 1, 32, r + 1, 44);
        ws.getCell(r + 1, 32).value =
          `Yükseklik (rakım) : ${d.yukseklik ? d.yukseklik + " m." : "................... m."}`;

        ws.mergeCells(r + 1, 45, r + 1, 56);
        ws.getCell(r + 1, 45).value =
          `Harita No : ${d.harita_no || "...................."}`;

        ws.mergeCells(r + 2, 1, r + 2, 23);
        ws.getCell(r + 2, 1).value =
          `Toprak Serisi : ${d.toprak_serisi || ".............................................................................."}`;

        ws.mergeCells(r + 2, 24, r + 2, 46);
        ws.getCell(r + 2, 24).value =
          `Coğrafi Konum : ${d.yeri || "..................................................."}`;

        ws.mergeCells(r + 2, 47, r + 3, 50);
        const koordinatCell = ws.getCell(r + 2, 47);
        koordinatCell.value = "Koordinat";
        koordinatCell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        koordinatCell.font = { name: "Calibri", size: 9, bold: true };

        ws.mergeCells(r + 2, 51, r + 2, 56);
        ws.getCell(r + 2, 51).value =
          `X : ${d.koordinat_x || "................."}`;

        ws.mergeCells(r + 3, 1, r + 3, 7);
        ws.getCell(r + 3, 1).value =
          `Ort. Sıcaklık : ${d.ort_sicaklik ? d.ort_sicaklik + " °C" : "....... °C"}`;

        ws.mergeCells(r + 3, 8, r + 3, 20);
        ws.getCell(r + 3, 8).value =
          `Ort. Toplam Yağış : ${d.ort_yagis ? d.ort_yagis + " mm." : ".............. mm."}`;

        ws.mergeCells(r + 3, 21, r + 3, 46);
        ws.getCell(r + 3, 21).value =
          `Yeri : ${d.il || ""} ${d.ilce || "...................................................................."}`;

        ws.mergeCells(r + 3, 51, r + 3, 56);
        ws.getCell(r + 3, 51).value =
          `Y : ${d.koordinat_y || "................."}`;

        // BAŞLIK BİLGİLERİ SATIR YÜKSEKLİĞİ -> 15.5
        for (let i = 0; i <= 3; i++) {
          const rowIdx = r + i;
          ws.getRow(rowIdx).height = 15.5;
          for (let c = 1; c <= 56; c++) {
            const cell = ws.getCell(rowIdx, c);
            if (cell.address !== ws.getCell(r + 2, 46).address) {
              cell.font = { name: "Calibri", size: 10, bold: true };
              cell.alignment = {
                vertical: "middle",
                horizontal: "left",
                wrapText: true,
              };
            }
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }
        }

        applyOuterFrame(ws, blockStartRow, r + 3, 1, 56);

        // Fizyografya ve Alt Tablo Oluşumu
        let fR = r + 4;
        ws.getRow(fR).height = 15.5;
        ws.mergeCells(fR, 1, fR, 56);
        const fizyoBaslik = ws.getCell(fR, 1);
        fizyoBaslik.value = "Fizyografya";
        fizyoBaslik.font = { name: "Calibri", size: 9, bold: true };
        fizyoBaslik.alignment = { vertical: "middle", horizontal: "center" };
        fizyoBaslik.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { arg: "FFEAEAEA" },
        };
        fizyoBaslik.border = {
          top: { style: "thick" },
          left: { style: "thick" },
          bottom: { style: "thin" },
          right: { style: "thick" },
        };

        let startRowFizyo = fR + 1;
        const secilenFizyo = d.fizyografya || "";
        const fizyoMatris = [
          [
            "DD1",
            "DY6",
            "Dsay",
            "TD1",
            "TY1",
            "TOut",
            "ES1",
            "ESCK",
            "EBj3",
            "VTrsv",
            "PTD",
            "PTly",
            "DZDsb",
            "DZGt",
            "DZGTrY2",
            "DLv",
            "DGSy",
          ],
          [
            "DD2",
            "DOu",
            "Dsoy",
            "TD2",
            "TY2",
            "TOusy",
            "ESy1",
            "EKOL",
            "Ely",
            "VTov",
            "PTY",
            "PTIkb",
            "DZDkt",
            "DZGd",
            "DZGTrY3",
            "DTk",
            "DGSk",
          ],
          [
            "DY1",
            "DOut",
            "DB",
            "TTd",
            "TY3",
            "TOv",
            "EC1",
            "EBk",
            "Elkb",
            "VDp",
            "PTE",
            "PTlytd",
            "DZDSy",
            "DZGdp",
            "DZGTrY4",
            "DZIy",
            "DGÇk",
          ],
          [
            "DY2",
            "DOusy",
            "DTd",
            "TB",
            "TY4",
            "TOd",
            "EEd1",
            "EByy",
            "Elytd",
            "VIs",
            "PTU1",
            "PTrsv",
            "DZDkk",
            "DZIykb",
            "DZGTrY5",
            "DZItd",
            "DGYp",
          ],
          [
            "DY3",
            "DOv",
            "DYvt",
            "TS",
            "TY5",
            "TYvt",
            "ES2",
            "Ebya",
            "Elysy",
            "VIkb",
            "PTU2",
            "PTC",
            "DZDktr",
            "DZIysv",
            "DZGOu",
            "DZItr",
            "TNZ",
          ],
          [
            "DY4",
            "DOd",
            "DYvsy",
            "Tsay",
            "TY6",
            "TYvsy",
            "EC2",
            "EBj1",
            "EDp",
            "VItd",
            "PTU3",
            "DZDo",
            "DZDktry",
            "DZGTru",
            "DZGOv",
            "DZTs",
          ],
          [
            "DY5",
            "DS",
            "DYvdt",
            "Tsoy",
            "TOu",
            "TYvdt",
            "EEd2",
            "EBj2",
            "VTru",
            "VIy",
            "PTO",
            "DZDd",
            "DZDSk",
            "DZGTrY1",
            "DZGOd",
          ],
        ];

        let isListedInGrid = false;
        fizyoMatris.forEach((rowCodes) => {
          rowCodes.forEach((code) => {
            if (
              secilenFizyo === code ||
              secilenFizyo.startsWith(code + " ") ||
              secilenFizyo.startsWith(code + "-")
            )
              isListedInGrid = true;
          });
        });

        fizyoMatris.forEach((rowCodes, rowIndex) => {
          const currRow = startRowFizyo + rowIndex;
          ws.getRow(currRow).height = 15.5;
          let currentCol = 1;

          rowCodes.forEach((code, colIndex) => {
            let colSpan = 3;
            if (colIndex === rowCodes.length - 1 && rowIndex < 5)
              colSpan = 56 - currentCol + 1;

            const isChecked =
              secilenFizyo === code ||
              secilenFizyo.startsWith(code + " ") ||
              secilenFizyo.startsWith(code + "-");
            const option = FIZYOGRAFYA_OPTIONS.find((o) => o.code === code);
            const labelText = option ? option.code : code;

            ws.mergeCells(
              currRow,
              currentCol,
              currRow,
              currentCol + colSpan - 1,
            );
            ws.getCell(currRow, currentCol).value =
              `${isChecked ? "■" : "□"} ${labelText}`;

            for (let i = currentCol; i < currentCol + colSpan; i++) {
              const cell = ws.getCell(currRow, i);
              cell.font = { name: "Calibri", size: 9 };
              cell.alignment = { vertical: "middle", horizontal: "left" };
              cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            }
            currentCol += colSpan;
          });

          if (rowIndex === 5) {
            ws.mergeCells(currRow, currentCol, currRow, 56);
            ws.getCell(currRow, currentCol).value = "Diğer:";
            for (let i = currentCol; i <= 56; i++) {
              const cell = ws.getCell(currRow, i);
              cell.font = { name: "Calibri", size: 9 };
              cell.alignment = { vertical: "bottom", horizontal: "left" };
              cell.border = {
                top: { style: "mediumDashed" },
                bottom: { style: "none" },
                left:
                  i === currentCol
                    ? { style: "mediumDashed" }
                    : { style: "none" },
                right: i === 56 ? { style: "thick" } : { style: "none" },
              };
            }
          }

          if (rowIndex === 6) {
            const digerChecked =
              !isListedInGrid && secilenFizyo !== "" && secilenFizyo !== null;
            const valText = digerChecked
              ? secilenFizyo
              : ".............................................";
            ws.mergeCells(currRow, currentCol, currRow, 56);
            ws.getCell(currRow, currentCol).value =
              `${digerChecked ? "■" : "□"} ${valText}`;
            for (let i = currentCol; i <= 56; i++) {
              const cell = ws.getCell(currRow, i);
              cell.font = { name: "Calibri", size: 9 };
              cell.alignment = { vertical: "middle", horizontal: "left" };
              cell.border = {
                top: { style: "none" },
                bottom: { style: "thick" },
                left:
                  i === currentCol
                    ? { style: "mediumDashed" }
                    : { style: "none" },
                right: i === 56 ? { style: "thick" } : { style: "none" },
              };
            }
          }
        });

        const modBorder = (row, col, updates) => {
          const c = ws.getCell(row, col);
          c.border = { ...c.border, ...updates };
        };

        for (let i = 0; i < 7; i++) {
          const currR = startRowFizyo + i;
          modBorder(currR, 1, { left: { style: "thick" } });
          modBorder(currR, 56, { right: { style: "thick" } });
          if (i === 6) {
            for (let col = 1; col <= 56; col++) {
              if (!ws.getCell(currR, col).value?.toString().includes("Diğer")) {
                modBorder(currR, col, { bottom: { style: "thick" } });
              }
            }
          }
        }

        let altTabloStart = startRowFizyo + 7;
        const lastRowUsed = appendDetayliOzelliklerTablosu(
          ws,
          altTabloStart,
          d,
        );

        // ==============================================================
        // --- SAYFA KESMESİ (SAYFA 1 SONU) ---
        // ==============================================================
        try {
          ws.getRow(lastRowUsed).addPageBreak();
        } catch (e) {}

        // ==============================================================
        // BÖLÜM 2: HORİZON TABLOSU (ARKA YÜZ - 2. SAYFA)
        // HORİZONA HİÇ DOKUNULMADI (Orijinal Yüksekliğinde Bırakıldı)
        // ==============================================================
        let hr = lastRowUsed + 2;

        ws.mergeCells(hr, 1, hr, 56);
        const hTitle = ws.getCell(hr, 1);
        hTitle.value = `PROFİL NO: ${profile.profil_no || "Belirtilmemiş"} - HORİZON BİLGİLERİ (ARKA YÜZ)`;
        hTitle.font = { bold: true, size: 12 };
        hTitle.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { arg: "FFEAEAEA" },
        };
        hTitle.alignment = { vertical: "middle", horizontal: "left" };
        ws.getRow(hr).height = 18;
        hr++;

        // Horizon Sütun Dağılımı (Toplamı 56 olacak şekilde)
        const hCols = [
          [1, 4, "HOR.", true],
          [5, 8, "DER. (cm)**", true],
          [9, 14, "SINIR", false],
          [15, 18, "RENK", true],
          [19, 23, "TEKSTÜR", true],
          [24, 30, "STRÜKTÜR", true],
          [31, 37, "KIVAM", true],
          [38, 41, "KİREÇ\n(köpürme)", true],
          [42, 44, "TAŞ.", true],
          [45, 53, "KÖK DAĞ.", false],
          [54, 56, "ÖZEL GÖRÜNÜMLER", true],
        ];

        hCols.forEach((col) => {
          if (col[3]) {
            ws.mergeCells(hr, col[0], hr + 1, col[1]);
          } else {
            ws.mergeCells(hr, col[0], hr, col[1]);
          }
          const c = ws.getCell(hr, col[0]);
          c.value = col[2];
          c.font = { bold: true, size: 8 };
          c.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };
        });

        // Alt Başlıklar
        ws.mergeCells(hr + 1, 9, hr + 1, 11);
        ws.getCell(hr + 1, 9).value = "Belirginlik";
        ws.mergeCells(hr + 1, 12, hr + 1, 14);
        ws.getCell(hr + 1, 12).value = "Şekil";
        ws.mergeCells(hr + 1, 45, hr + 1, 47);
        ws.getCell(hr + 1, 45).value = "ÇEŞİT";
        ws.mergeCells(hr + 1, 48, hr + 1, 50);
        ws.getCell(hr + 1, 48).value = "MİKTAR";
        ws.mergeCells(hr + 1, 51, hr + 1, 53);
        ws.getCell(hr + 1, 51).value = "KALINLIK";

        [hr, hr + 1].forEach((rowIdx) => {
          ws.getRow(rowIdx).height = 16;
          for (let ci = 1; ci <= 56; ci++) {
            const cell = ws.getCell(rowIdx, ci);
            if (!cell.font) cell.font = { bold: true, size: 8 };
            if (!cell.alignment)
              cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true,
              };
            cell.border = {
              top: { style: "thick" },
              bottom: { style: "thick" },
              left: { style: "thin" },
              right: { style: "thin" },
            };
          }
        });

        hr += 2;
        const hBlockStart = hr;
        const horizons = d.horizons || [];

        if (horizons.length === 0) {
          ws.mergeCells(hr, 1, hr, 56);
          ws.getCell(hr, 1).value =
            "Bu profil için girilmiş horizon verisi bulunamadı.";
          ws.getCell(hr, 1).alignment = { horizontal: "center" };
          hr++;
        } else {
          horizons.forEach((h) => {
            const sinirBelText = hlSmart(
              ["keskin", "belirli", "geçişli", "yaygın"],
              h.sinir_belirginlik,
              ["Keskin", "Belirli", "Geçişli", "Yaygın"],
              "\n",
            );
            const sinirSekilText = hlSmart(
              ["düz", "dalgalı", "düzensiz", "kırıklı"],
              h.sinir_sekil,
              ["Düz", "Dalgalı", "Düzensiz", "Kırıklı"],
              "\n",
            );
            const teksturText = hlSmart(
              [
                "C",
                "CL",
                "FLS",
                "L",
                "LFS",
                "LS",
                "S",
                "SC",
                "SCL",
                "SIC",
                "SICL",
                "SIL",
                "SL",
                "VFLS",
              ],
              h.tekstur,
              [
                "C (Kil)",
                "CL (Killi Tın)",
                "FLS (İnce Tınlı Kum)",
                "L (Tın)",
                "LFS (Tınlı İnce Kum)",
                "LS (Tınlı Kum)",
                "S (Kum)",
                "SC (Kumlu Kil)",
                "SCL (Kumlu Killi Tın)",
                "SIC (Siltli Kil)",
                "SICL (Siltli Killi Tın)",
                "SIL (Siltli Tın)",
                "SL (Kumlu Tın)",
                "VFLS (Çok İnce Tınlı Kum)",
              ],
              " - ",
            );

            const strDayText = `DAY: ${hlSmart(["ç.zayıf", "zayıf", "orta", "kuvvetli", "ç.kuvvetli"], h.struktur_dayaniklilik, ["Çok Zayıf", "Zayıf", "Orta", "Kuvvetli", "Çok Kuvvetli"], " - ")}`;
            const strBuyText = `BÜY: ${hlSmart(["ç.küçük", "küçük", "orta", "kaba", "ç.kaba"], h.struktur_buyukluk, ["Çok Küçük / Çok İnce", "Küçük / İnce", "Orta", "Kaba / Kalın", "Çok Kaba / Çok Kalın"], " - ")}`;
            const strTipText = `TİP: ${hlSmart(["furda", "granüler", "levha", "priz.kol.", "sütunsu", "köş.blok", "yr.köş.blok", "masif", "taneli"], h.struktur_tip, ["Furda", "Granüler", "Levha", "Prizmatik", "Sütunsu", "Köşeli Blok", "Yarı Köşeli Blok", "Masif", "Taneli"], " - ")}`;
            const strukturAll = `${strDayText}\n${strBuyText}\n${strTipText}`;

            const kivamKuruText = `K: ${hlSmart(["dağınık", "yumuşak", "h.sert", "sert", "çok sert"], h.kivam_kuru, ["Dağınık", "Yumuşak", "Hafif Sert", "Sert", "Çok Sert"], " - ")}`;
            const kivamNemliText = `N: ${hlSmart(["dağınık", "gevşek", "sıkı", "çok sıkı", "aşırı sıkı"], h.kivam_nemli, ["Dağınık", "Gevşek", "Sıkı", "Çok Sıkı", "Aşırı Sıkı"], " - ")}`;
            const kYasYapText = hlSmart(
              ["yap.değil", "az yap.", "yap.", "ç.yap."],
              h.kivam_yas_yapiskanlik,
              ["Yapışkan Değil", "Az Yapışkan", "Yapışkan", "Çok Yapışkan"],
              " - ",
            );
            const kYasPlasText = hlSmart(
              ["plas. değ.", "az plas.", "plastik", "ç.plastik"],
              h.kivam_yas_plastiklik,
              ["Plastik Değil", "Az Plastik", "Plastik", "Çok Plastik"],
              " - ",
            );
            const kivamAll = `${kivamKuruText}\n${kivamNemliText}\nY: ${kYasYapText} / ${kYasPlasText}`;

            const kirecText = hlSmart(
              ["Hiç Yok", "Görünebilir", "Zayıf", "Kuvvetli"],
              h.kirec,
              ["Hiç Yok", "Görünebilir", "Zayıf", "Kuvvetli"],
              "\n",
            );
            const tasText = hlSmart(
              ["taşsız", "az taşlı", "taşlı", "çok taşlı"],
              h.taslilik,
              ["Taşsız", "Az Taşlı", "Taşlı", "Çok Taşlı"],
              "\n",
            );

            const kCesitArr = [];
            if (h.kok_sacak_miktar && h.kok_sacak_miktar !== "Yok")
              kCesitArr.push("Saçak");
            if (h.kok_kazik_miktar && h.kok_kazik_miktar !== "Yok")
              kCesitArr.push("Kazık");
            const cesitText = hlSmart(
              ["saçak", "kazık"],
              kCesitArr,
              ["Saçak", "Kazık"],
              "\n\n",
            );

            const mMik1 = hlSmart(
              ["ç.sey.", "seyrek", "yaygın", "ç.yaygın"],
              h.kok_sacak_miktar,
              ["Çok Seyrek", "Seyrek", "Yaygın", "Çok Yaygın"],
              "\n",
            );
            const mMik2 = hlSmart(
              ["ç.sey.", "seyrek", "yaygın", "ç.yaygın"],
              h.kok_kazik_miktar,
              ["Çok Seyrek", "Seyrek", "Yaygın", "Çok Yaygın"],
              "\n",
            );
            const miktarText = `${mMik1}\n---\n${mMik2}`;

            const mKal1 = hlSmart(
              ["ç.ince", "ince", "orta", "kaba", "ç.kaba"],
              h.kok_sacak_kalinlik,
              ["Çok İnce", "İnce", "Orta", "Kaba", "Çok Kaba"],
              "\n",
            );
            const mKal2 = hlSmart(
              ["ç.ince", "ince", "orta", "kaba", "ç.kaba"],
              h.kok_kazik_kalinlik,
              ["Çok İnce", "İnce", "Orta", "Kaba", "Çok Kaba"],
              "\n",
            );
            const kalinlikText = `${mKal1}\n---\n${mKal2}`;

            ws.mergeCells(hr, 1, hr, 4);
            ws.getCell(hr, 1).value = h.sembol || "";
            ws.mergeCells(hr, 5, hr, 8);
            ws.getCell(hr, 5).value =
              `${h.derinlik_min || 0} - ${h.derinlik_max || ""}${h.derinlik_arti ? "+" : ""}`;
            ws.mergeCells(hr, 9, hr, 11);
            ws.getCell(hr, 9).value = sinirBelText;
            ws.mergeCells(hr, 12, hr, 14);
            ws.getCell(hr, 12).value = sinirSekilText;
            ws.mergeCells(hr, 15, hr, 18);
            ws.getCell(hr, 15).value =
              `Kuru:\n${h.renk_sinif_kuru || ""} ${h.renk_kod_kuru || ""}\nNemli:\n${h.renk_sinif_yas || ""} ${h.renk_kod_yas || ""}`;
            ws.mergeCells(hr, 19, hr, 23);
            ws.getCell(hr, 19).value = teksturText;
            ws.mergeCells(hr, 24, hr, 30);
            ws.getCell(hr, 24).value = strukturAll;
            ws.mergeCells(hr, 31, hr, 37);
            ws.getCell(hr, 31).value = kivamAll;
            ws.mergeCells(hr, 38, hr, 41);
            ws.getCell(hr, 38).value = kirecText;
            ws.mergeCells(hr, 42, hr, 44);
            ws.getCell(hr, 42).value = tasText;
            ws.mergeCells(hr, 45, hr, 47);
            ws.getCell(hr, 45).value = cesitText;
            ws.mergeCells(hr, 48, hr, 50);
            ws.getCell(hr, 48).value = miktarText;
            ws.mergeCells(hr, 51, hr, 53);
            ws.getCell(hr, 51).value = kalinlikText;
            ws.mergeCells(hr, 54, hr, 56);
            ws.getCell(hr, 54).value = h.ozel_gorunum || "";

            // HORİZON ORİJİNAL YÜKSEKLİĞİ KORUNDU
            ws.getRow(hr).height = 70;
            for (let ci = 1; ci <= 56; ci++) {
              const cell = ws.getCell(hr, ci);
              cell.font = { size: 7.5, name: "Calibri" };
              cell.alignment = {
                wrapText: true,
                vertical: "middle",
                horizontal: "center",
              };
              cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            }
            hr++;
          });
        }

        applyOuterFrame(ws, hBlockStart - 2, hr - 1, 1, 56);

        // ==============================================================
        // --- SAYFA KESMESİ (SAYFA 2 SONU) ---
        // ==============================================================
        try {
          ws.getRow(hr).addPageBreak();
        } catch (e) {}

        currentStartRow = hr + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        dosyaAdi(jobName, "Profil Defteri 1", "xlsx"),
      );
    } catch (error) {
      console.error("Form Export Hatası:", error);
      alert("Rapor indirilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Tooltip title="Profil Defteri 1 İndir (Yatay A4 - Baskıya Hazır)">
        <IconButton
          onClick={handleExport}
          disabled={loading}
          sx={{
            bgcolor: alpha("#009688", 0.1),
            color: "#009688",
            mb: 0.5,
            "&:hover": { bgcolor: alpha("#009688", 0.2) },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Feed fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{ fontSize: "0.6rem", color: "text.secondary", fontWeight: "bold" }}
      >
        PROFİL DEFTERİ-1
      </Typography>
    </Box>
  );
}
